/**
 * Buyback-specific form data builders.
 *
 * Builds guardrails and attributes for BUYBACK campaign type
 * from form state. Extracted from formDataBuilder for single-responsibility.
 *
 * @module buybackDataBuilder
 */

import {
    BrandSweetenerOption,
    CurrencyCode,
} from 'src/@types/valueMatching/enums';
import { BuybackGuardrail } from 'src/@types/valueMatching/guardrails';
import { BuybackAttributes } from 'src/@types/valueMatching/attributes';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { parseOptionalNumber } from 'src/utils/helpers';
import { parseAsins } from '../formDataBuilder';

// ============================================================================
// Public API
// ============================================================================

/** Builds Buyback guardrail from form state. */
export function buildBuybackGuardrail(form: UseCampaignFormResult): BuybackGuardrail {
    const guardrail: BuybackGuardrail = {};

    const percentOfAsinPrice = parseOptionalNumber(form.buybackPercentOfAsinPrice);
    if (percentOfAsinPrice != null) {
        guardrail.percentOfASINPriceGuardrail = percentOfAsinPrice;
    }

    const percentOfBuybackOffer = parseOptionalNumber(form.buybackPercentOfBuybackOffer);
    if (percentOfBuybackOffer != null) {
        guardrail.percentOfBuybackOfferGuardrail = percentOfBuybackOffer;
    }

    const absoluteValueCap = parseOptionalNumber(form.buybackAbsoluteValueCap);
    if (absoluteValueCap != null) {
        guardrail.absoluteValueGuardrail = {
            currencyValue: absoluteValueCap,
            currencyCode: CurrencyCode.INR,
        };
    }

    return guardrail;
}

/** Builds Buyback attributes from form state. */
export function buildBuybackAttributes(form: UseCampaignFormResult): BuybackAttributes {
    return {
        // Reuse parseAsins for consistent delimiter handling (commas, newlines, CR)
        excludedOldItemIDs: parseAsins(form.excludedOldItemIds ?? ''),
        includeFixedValueSweetener: form.brandSweetenerOption === BrandSweetenerOption.YES,
    };
}
